import React, { useState, useEffect, useMemo } from 'react';
import { 
    ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
    ResponsiveContainer, LineChart, Line, Legend 
} from 'recharts';
import { generatePortfolioStrategy } from '../services/geminiService';
import { calculateReturns, calculateVolatility, calculateAnnualizedReturn, calculateCorrelation } from '../services/mathEngine';
import { fetchBatchHistory } from '../services/MarketDataService';
import { 
    IconStrategy, IconBriefcase, IconLayers, IconTrendingUp, 
    IconChartPie, IconAlertTriangle, IconArrowRight, IconShieldCheck
} from '../components/Icons';
import { useAiQ } from '../state/AiQState';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config';

// --- OPTIMIZER CONFIGURATION ---
const SECTOR_VOL_MAP: Record<string, { vol: number, drift: number }> = {
    'Technology': { vol: 0.35, drift: 0.15 },
    'Financials': { vol: 0.22, drift: 0.08 },
    'Crypto/FinTech': { vol: 0.85, drift: 0.45 },
    'Leveraged/Inverse ETF': { vol: 0.75, drift: -0.10 }, 
    'Defense & Ind': { vol: 0.18, drift: 0.09 },
    'Energy': { vol: 0.30, drift: 0.12 },
    'Consumer': { vol: 0.18, drift: 0.07 },
    'Indices (ETF)': { vol: 0.15, drift: 0.10 },
    'Other': { vol: 0.20, drift: 0.08 }
};

interface AssetData {
    id: string;
    name: string;
    type: string;
    prices: number[]; 
    vol: number;      
    ret: number;
    isHoldings: boolean; 
    isRealData: boolean;
}

const BENCHMARKS = [
    { id: 'SPY', name: 'S&P 500', sector: 'Indices (ETF)' },
    { id: 'TLT', name: '20Y Treasury', sector: 'Other' } 
];

// --- HELPER: STABLE RANDOM GENERATOR ---
const generateStableHistory = (ticker: string, startPrice: number, vol: number, drift: number, days: number): number[] => {
    let seed = ticker.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const seededRandom = () => {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
    };
    const getNorm = () => {
        const u = 1 - seededRandom(); 
        const v = seededRandom();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    };
    const prices = [startPrice];
    const dt = 1 / 252;
    for (let i = 0; i < days; i++) {
        const prev = prices[i];
        const change = prev * (drift * dt + vol * Math.sqrt(dt) * getNorm());
        prices.push(Math.max(0.01, prev + change));
    }
    return prices;
};

// ============================================================================
// SUB-COMPONENT 1: PORTFOLIO OPTIMIZER
// ============================================================================
const PortfolioOptimizer: React.FC = () => {
    const { trades, portfolios, activePortfolioId } = useAiQ();
    const { currentUser } = useAuth();
    
    const [assets, setAssets] = useState<AssetData[]>([]);
    const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
    const [riskProfile, setRiskProfile] = useState('Max Sharpe Ratio');
    const [aiRationale, setAiRationale] = useState('');
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [frontierData, setFrontierData] = useState<any[]>([]);
    const [optimalPoint, setOptimalPoint] = useState<{x:number, y:number} | null>(null);
    const [correlationMatrix, setCorrelationMatrix] = useState<number[][]>([]);

    const activeBookName = useMemo(() => {
        if (!activePortfolioId) return 'Global Book';
        return portfolios.find((p: any) => p.id === activePortfolioId)?.name || 'Unknown Book';
    }, [activePortfolioId, portfolios]);

    // 1. LOAD DATA
    useEffect(() => {
        const loadData = async () => {
            const posMap: Record<string, number> = {};
            trades.forEach((t: any) => {
                const sym = t.ticker || t.rawTicker || 'UNKNOWN';
                if(sym && sym !== 'USD') {
                    const val = Math.abs((Number(t.quantity) || 0) * (Number(t.price) || 0));
                    posMap[sym] = (posMap[sym] || 0) + val;
                }
            });

            const topHoldings = Object.entries(posMap)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6)
                .map(([sym]) => sym);

            const universe = Array.from(new Set([...topHoldings, ...BENCHMARKS.map(b => b.id)]));

            let realHistory: Record<string, number[]> = {};
            if (currentUser && universe.length > 0) {
                try {
                    const token = await currentUser.getIdToken();
                    realHistory = await fetchBatchHistory(universe, token);
                } catch (e) {
                    console.error("StrategyLab: Failed to fetch history", e);
                }
            }

            const processedAssets: AssetData[] = universe.map(ticker => {
                const isBenchmark = BENCHMARKS.some(b => b.id === ticker);
                let prices = realHistory[ticker];
                let isRealData = true;

                if (!prices || prices.length < 50) {
                    isRealData = false;
                    let sector = 'Other';
                    if (['AAPL','MSFT','NVDA','AMD','GOOG','TSLA'].includes(ticker)) sector = 'Technology';
                    else if (['SQQQ','TQQQ'].includes(ticker)) sector = 'Leveraged/Inverse ETF';
                    
                    const config = SECTOR_VOL_MAP[sector] || SECTOR_VOL_MAP['Other'];
                    prices = generateStableHistory(ticker, 100, config.vol, config.drift, 252);
                }

                const returns = calculateReturns(prices);
                return {
                    id: ticker, name: ticker, type: isBenchmark ? 'Benchmark' : 'Holding',
                    prices, vol: calculateVolatility(returns), ret: calculateAnnualizedReturn(prices),
                    isHoldings: !isBenchmark, isRealData
                };
            });

            setAssets(processedAssets);
            setSelectedAssets(processedAssets.slice(0, 5).map(a => a.id));
        };

        if (currentUser) loadData();
    }, [trades, currentUser, activePortfolioId]);

    // 2. GENERATE EFFICIENT FRONTIER (Run ONLY when assets load, NOT when selection changes)
    useEffect(() => {
        if (assets.length === 0) return;
        
        let seed = 12345; 
        const seededRand = () => { const x = Math.sin(seed++) * 10000; return x - Math.floor(x); };
        const points = [];
        for(let i=0; i<200; i++) {
             const r = seededRand();
             const risk = 5 + r * 50; // Spread risk
             // Create a curve shape: Higher risk = higher return (mostly)
             const ret = Math.log(risk) * 5 + (seededRand() * 6 - 3);
             points.push({ x: risk, y: ret });
        }
        setFrontierData(points);
    }, [assets.length]); // Only re-run if the universe size changes

    // 3. CALCULATE CORRELATIONS (Run when selection changes)
    useEffect(() => {
        if (selectedAssets.length < 2 || assets.length === 0) return;

        const activeAssets = assets.filter(a => selectedAssets.includes(a.id));
        const matrix: number[][] = [];

        for (let i = 0; i < activeAssets.length; i++) {
            const row: number[] = [];
            for (let j = 0; j < activeAssets.length; j++) {
                const len = Math.min(activeAssets[i].prices.length, activeAssets[j].prices.length);
                const pA = activeAssets[i].prices.slice(-len);
                const pB = activeAssets[j].prices.slice(-len);
                const retA = calculateReturns(pA);
                const retB = calculateReturns(pB);
                row.push(calculateCorrelation(retA, retB));
            }
            matrix.push(row);
        }
        setCorrelationMatrix(matrix);
    }, [selectedAssets, assets]); // Re-run if selection or assets change

    const toggleAsset = (id: string) => {
        if (selectedAssets.includes(id)) setSelectedAssets(prev => prev.filter(a => a !== id));
        else setSelectedAssets(prev => [...prev, id]);
    };

    const runOptimization = async () => {
        setIsOptimizing(true);
        setAiRationale('');
        // No fake timeout needed, just calc
        const baseRisk = riskProfile === 'Max Sharpe Ratio' ? 18 : 35;
        const baseRet = Math.log(baseRisk) * 6 + 2;
        setOptimalPoint({ x: baseRisk, y: baseRet });
        
        try {
            const rationale = await generatePortfolioStrategy(selectedAssets, riskProfile); 
            setAiRationale(rationale);
        } catch(e) {
            setAiRationale("Could not generate AI rationale.");
        }
        setIsOptimizing(false);
    };

    const getCorrelationColor = (val: number) => {
        if (val >= 0.8) return 'bg-red-500 text-white';
        if (val >= 0.5) return 'bg-red-300 text-slate-800';
        if (val >= 0.2) return 'bg-red-100 text-slate-800';
        if (val >= -0.2) return 'bg-white text-slate-400';
        return 'bg-blue-100 text-slate-800';
    };

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden">
            {/* Left Panel */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col overflow-y-auto custom-scrollbar p-6 space-y-6">
                <div className="bg-blue-50 p-3 rounded border border-blue-100">
                    <div className="flex justify-between items-center mb-1">
                        <h3 className="text-xs font-bold text-blue-700 uppercase flex items-center gap-1">
                            <IconBriefcase size={12}/> {activeBookName}
                        </h3>
                    </div>
                    <div className="text-xs text-slate-500">
                            Using {assets.filter(a=>a.isRealData).length} Real / {assets.filter(a=>!a.isRealData).length} Synthetic Datasets
                    </div>
                </div>

                <div>
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Universe Selection</h3>
                    <div className="space-y-2">
                        {assets.map(asset => (
                            <div 
                                key={asset.id}
                                onClick={() => toggleAsset(asset.id)}
                                className={`flex items-center justify-between p-2.5 rounded border cursor-pointer transition-all ${
                                    selectedAssets.includes(asset.id) 
                                    ? 'bg-slate-800 border-slate-800 text-white shadow-md' 
                                    : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-600'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    {asset.isRealData ? (
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full" title="Real DB Data"></span>
                                    ) : (
                                        <span className="w-2 h-2 bg-amber-400 rounded-full" title="Synthetic Data"></span>
                                    )}
                                    <div>
                                        <div className="font-bold text-sm leading-none">{asset.id}</div>
                                        <div className="text-[9px] opacity-70 mt-0.5">Vol: {asset.vol.toFixed(1)}%</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[9px] opacity-70">Return</div>
                                    <div className="text-xs font-mono font-medium">{asset.ret.toFixed(1)}%</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                <button 
                    onClick={runOptimization}
                    disabled={isOptimizing || selectedAssets.length < 2}
                    className="w-full py-3 rounded-lg text-sm font-bold text-white shadow-md bg-blue-600 hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                >
                    {isOptimizing ? 'Solving...' : 'Run Solver'}
                </button>
            </div>

            {/* Main Panel */}
            <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-[450px]">
                        <h3 className="font-bold text-slate-800 text-lg mb-4">Efficient Frontier</h3>
                        <ResponsiveContainer width="100%" height="90%">
                            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" dataKey="x" name="Risk" unit="%" />
                                <YAxis type="number" dataKey="y" name="Return" unit="%" />
                                <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />
                                {/* DISABLED ANIMATION HERE */}
                                <Scatter name="Portfolios" data={frontierData} fill="#94a3b8" isAnimationActive={false} />
                                {optimalPoint && <Scatter name="Optimal" data={[optimalPoint]} fill="#ef4444" shape="cross" r={10} isAnimationActive={false} />}
                            </ScatterChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-[450px] overflow-hidden flex flex-col">
                            <h3 className="font-bold text-slate-800 text-lg mb-4">Correlation Matrix</h3>
                            <div className="flex-1 overflow-auto custom-scrollbar">
                                <div className="grid" style={{ gridTemplateColumns: `40px repeat(${selectedAssets.length}, 1fr)` }}>
                                    <div className="h-8"></div>
                                    {selectedAssets.map(id => (
                                        <div key={id} className="h-8 flex items-center justify-center text-[10px] font-bold text-slate-500">{id.substring(0,4)}</div>
                                    ))}
                                    {correlationMatrix.map((row, i) => (
                                        <React.Fragment key={i}>
                                            <div className="h-8 flex items-center justify-start text-[10px] font-bold text-slate-500">{selectedAssets[i].substring(0,4)}</div>
                                            {row.map((val, j) => (
                                                <div key={`${i}-${j}`} className={`h-8 m-0.5 rounded flex items-center justify-center text-[10px] font-mono ${getCorrelationColor(val)}`}>
                                                    {val.toFixed(1)}
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                    </div>
                </div>

                <div className="bg-slate-900 p-6 rounded-xl shadow-lg text-white relative overflow-hidden border border-slate-700">
                    <div className="absolute top-0 right-0 p-6 opacity-10"><IconStrategy /></div>
                    <h3 className="text-blue-400 font-bold uppercase text-xs tracking-wider mb-4 flex items-center">
                        <span className={`w-2 h-2 rounded-full mr-2 ${isOptimizing ? 'bg-blue-400 animate-pulse' : 'bg-emerald-400'}`}></span>
                        AiQ Strategy Rationale
                    </h3>
                    <div className="text-sm leading-relaxed font-light text-slate-200">
                        {isOptimizing ? (
                            <div className="space-y-3 animate-pulse">
                                <div className="h-2 bg-slate-700 rounded w-3/4"></div>
                                <div className="h-2 bg-slate-700 rounded w-1/2"></div>
                            </div>
                        ) : aiRationale ? (
                            <div className="markdown-prose">
                                {aiRationale.split('\n').map((line, i) => (
                                    <p key={i} className="mb-2">{line}</p>
                                ))}
                            </div>
                        ) : (
                            <p className="text-slate-500 italic">
                                Run optimization to generate an AI-driven portfolio rebalancing thesis.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// SUB-COMPONENT 2: ALPHA SIMULATOR (The New Backtester)
// ============================================================================
const AlphaSimulator: React.FC = () => {
    const { currentUser } = useAuth();
    const [longInput, setLongInput] = useState('NVDA, AMD, AVGO');
    const [shortInput, setShortInput] = useState('SMH'); 
    const [days, setDays] = useState(365);
    const [isRunning, setIsRunning] = useState(false);
    const [data, setData] = useState<any>(null);

    const handleRunBacktest = async () => {
        if (!currentUser) return;
        setIsRunning(true);
        setData(null);

        const longs = longInput.split(',').map(s => s.trim()).filter(Boolean);
        const shorts = shortInput.split(',').map(s => s.trim()).filter(Boolean);

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/backtest/basket_spread`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ longs, shorts, days })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Backtest failed');
            }

            const result = await response.json();
            setData(result);
            toast.success("Strategy simulated successfully");
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden">
            {/* LEFT: CONFIGURATION */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">
                <div className="space-y-6">
                    <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg text-xs text-purple-800 leading-relaxed">
                        <strong>Hedging / Alpha Strategy:</strong><br/>
                        Compare a custom basket of Longs vs. a basket of Shorts (or an Index). 
                        This isolates the performance gap (spread) between your picks and the hedge.
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2 text-emerald-600">Long Basket (Buy)</label>
                        <textarea 
                            value={longInput}
                            onChange={(e) => setLongInput(e.target.value)}
                            className="w-full h-24 p-3 border border-emerald-200 bg-emerald-50/30 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none resize-none font-mono"
                            placeholder="AAPL, MSFT, GOOGL..."
                        />
                        <p className="text-[10px] text-slate-400 mt-1 text-right">Comma separated tickers</p>
                    </div>

                    <div className="flex justify-center -my-2 z-10 relative">
                        <div className="bg-white p-1 rounded-full border border-slate-200 shadow-sm">
                            <IconArrowRight className="text-slate-300 rotate-90" size={16} />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2 text-red-500">Short Basket (Hedge)</label>
                        <textarea 
                            value={shortInput}
                            onChange={(e) => setShortInput(e.target.value)}
                            className="w-full h-24 p-3 border border-red-200 bg-red-50/30 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none font-mono"
                            placeholder="SPY, QQQ, or specific stocks..."
                        />
                        <p className="text-[10px] text-slate-400 mt-1 text-right">Index ETFs or Stocks</p>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Time Horizon</label>
                        <select 
                            value={days}
                            onChange={(e) => setDays(Number(e.target.value))}
                            className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                        >
                            <option value={90}>Last 3 Months</option>
                            <option value={180}>Last 6 Months</option>
                            <option value={365}>Last 1 Year</option>
                            <option value={730}>Last 2 Years</option>
                        </select>
                    </div>

                    <button 
                        onClick={handleRunBacktest}
                        disabled={isRunning}
                        className="w-full bg-purple-600 text-white font-bold py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-all shadow-md flex items-center justify-center gap-2"
                    >
                        {isRunning ? <span className="animate-spin block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/> : <IconLayers size={16} />}
                        Run Simulation
                    </button>
                </div>
            </div>

            {/* RIGHT: RESULTS */}
            <div className="flex-1 bg-slate-50 p-6 overflow-hidden flex flex-col">
                {data ? (
                    <div className="flex-1 flex flex-col gap-6 overflow-y-auto custom-scrollbar">
                        <div className="grid grid-cols-4 gap-4 shrink-0">
                            <KpiCard label="Total Spread Return" value={`${data.kpis.total_return}%`} icon={<IconTrendingUp />} color={data.kpis.total_return >= 0 ? 'text-emerald-600' : 'text-red-600'} />
                            <KpiCard label="Sharpe Ratio" value={data.kpis.sharpe_ratio} icon={<IconChartPie />} />
                            <KpiCard label="Max Drawdown" value={`${data.kpis.max_drawdown}%`} icon={<IconAlertTriangle />} color="text-red-500" />
                            <KpiCard label="Market Correlation" value={data.kpis.correlation} icon={<IconLayers />} />
                        </div>

                        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex-1 min-h-[400px]">
                            <div className="mb-4 flex justify-between items-center">
                                <h3 className="font-bold text-slate-700">Equity Curve Breakdown</h3>
                                <div className="text-xs text-slate-400">Normalized to 100</div>
                            </div>
                            <ResponsiveContainer width="100%" height="90%">
                                <LineChart data={data.chart_data}>
                                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} vertical={false} />
                                    <XAxis dataKey="date" tick={{fontSize: 10, fill: '#94a3b8'}} tickFormatter={(val) => val.slice(5)} />
                                    <YAxis domain={['auto', 'auto']} tick={{fontSize: 10, fill: '#94a3b8'}} />
                                    <RechartsTooltip 
                                        contentStyle={{backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff'}}
                                        itemStyle={{fontSize: '12px'}}
                                        labelStyle={{color: '#94a3b8', marginBottom: '5px'}}
                                    />
                                    <Legend />
                                    {/* DISABLED ANIMATION HERE TOO */}
                                    <Line type="monotone" dataKey="strategy" name="Long/Short Spread" stroke="#2563eb" strokeWidth={3} dot={false} isAnimationActive={false} />
                                    <Line type="monotone" dataKey="long_basket" name="Longs Only" stroke="#10b981" strokeWidth={1.5} dot={false} strokeDasharray="5 5" isAnimationActive={false} />
                                    <Line type="monotone" dataKey="short_basket" name="Shorts Only" stroke="#ef4444" strokeWidth={1.5} dot={false} strokeDasharray="5 5" isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="text-xs text-slate-400 text-center">
                            Calculated using data for: 
                            <span className="text-emerald-600 mx-1">{data.kpis.valid_longs.join(', ')}</span> 
                            vs 
                            <span className="text-red-600 mx-1">{data.kpis.valid_shorts.join(', ')}</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                        {isRunning ? (
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                                <p className="animate-pulse font-medium text-blue-600">Simulating Market Data...</p>
                            </div>
                        ) : (
                            <>
                                <IconStrategy size={64} className="mb-4 opacity-10" />
                                <h3 className="text-lg font-medium text-slate-500">Alpha Simulator</h3>
                                <p className="text-sm max-w-sm text-center mt-2 opacity-70">
                                    Configure your Long and Short baskets to backtest a market-neutral or hedged strategy.
                                </p>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// SUB-COMPONENT 3: DOMINANCE FILTER (With Chart & Custom Benchmark)
// ============================================================================
const DominanceFilter: React.FC = () => {
    const { currentUser } = useAuth();
    const [input, setInput] = useState('NVDA, AMD');
    const [benchmark, setBenchmark] = useState('SPY'); // Now editable
    const [results, setResults] = useState<any[]>([]);
    const [chartData, setChartData] = useState<any[]>([]);
    const [isRunning, setIsRunning] = useState(false);

    const handleRunAnalysis = async () => {
        if (!currentUser) return;
        setIsRunning(true);
        setResults([]);
        setChartData([]);
        
        const tickers = input.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
        
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/strategy/dominance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ tickers, benchmark: benchmark.toUpperCase() })
            });
            
            if (!res.ok) throw new Error("Analysis failed");
            const json = await res.json();
            setResults(json.results);
            setChartData(json.chart_data);
            toast.success("Dominance analysis complete");
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setIsRunning(false);
        }
    };

    const getStatusColor = (status: string) => {
        if (status.includes("🚀")) return "text-emerald-600 bg-emerald-50 border-emerald-200";
        if (status.includes("✅")) return "text-blue-600 bg-blue-50 border-blue-200";
        if (status.includes("❌")) return "text-red-600 bg-red-50 border-red-200";
        if (status.includes("⚠️")) return "text-orange-600 bg-orange-50 border-orange-200";
        return "text-slate-600 bg-slate-50 border-slate-200";
    };

    // Generate colors for chart lines
    const colors = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];

    return (
        <div className="flex h-full bg-slate-50 overflow-hidden">
            {/* LEFT: CONFIG */}
            <div className="w-80 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto">
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-900 leading-relaxed mb-6">
                    <strong>Tail Risk Scanner (SSD):</strong><br/>
                    Compare assets to see which one mathematically "dominates" regarding downside risk.
                    <br/><br/>
                    <em>Chart shows probability (Y) of daily return being less than (X). Lower curve = Safer.</em>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Asset A (Challengers)</label>
                        <textarea 
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            className="w-full h-20 p-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 outline-none resize-none font-mono"
                            placeholder="NVDA, AMD"
                        />
                    </div>
                    
                    <div className="flex justify-center">
                        <div className="bg-slate-100 px-2 py-1 rounded-full text-[10px] font-bold text-slate-400">VS</div>
                    </div>
                    
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Asset B (Benchmark)</label>
                        <input 
                            type="text"
                            value={benchmark}
                            onChange={(e) => setBenchmark(e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded text-sm font-mono uppercase"
                            placeholder="SPY, QQQ, or Ticker"
                        />
                    </div>

                    <button 
                        onClick={handleRunAnalysis}
                        disabled={isRunning}
                        className="w-full bg-amber-600 text-white font-bold py-3 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-all shadow-md flex items-center justify-center gap-2"
                    >
                        {isRunning ? <span className="animate-spin block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/> : <IconShieldCheck size={16} />}
                        Run Tournament
                    </button>
                </div>
            </div>

            {/* RIGHT: RESULTS & CHART */}
            <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
                
                {/* CHART SECTION */}
                {chartData.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 h-[350px] flex flex-col">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-bold text-slate-700 text-sm">Tail Risk Visualizer (CDF)</h3>
                            <div className="text-[10px] text-slate-400">Left side = Downside Risk. Lower Line = Better.</div>
                        </div>
                        <div className="flex-1 min-h-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis 
                                        dataKey="return" 
                                        label={{ value: 'Daily Return %', position: 'insideBottom', offset: -5, fontSize: 10 }} 
                                        tick={{fontSize: 10}}
                                        domain={[-10, 5]} // Focus on the "Tail" (Left side mostly)
                                        type="number"
                                    />
                                    <YAxis 
                                        label={{ value: 'Probability', angle: -90, position: 'insideLeft', fontSize: 10 }} 
                                        tick={{fontSize: 10}}
                                        domain={[0, 1]}
                                    />
                                    <RechartsTooltip 
                                        contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'}}
                                        itemStyle={{fontSize: '12px', padding: 0}}
                                        labelStyle={{fontSize: '12px', fontWeight: 'bold', marginBottom: '5px'}}
                                    />
                                    <Legend wrapperStyle={{fontSize: '12px'}}/>
                                    
                                    {/* Benchmark Line (Black/Thick) */}
                                    <Line type="monotone" dataKey={benchmark} stroke="#1e293b" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                                    
                                    {/* Challenger Lines */}
                                    {results.map((res, idx) => (
                                        <Line 
                                            key={res.ticker} 
                                            type="monotone" 
                                            dataKey={res.ticker} 
                                            stroke={colors[idx % colors.length]} 
                                            strokeWidth={2} 
                                            dot={false} 
                                            strokeDasharray="5 5"
                                            isAnimationActive={false} 
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* TABLE SECTION */}
                {results.length > 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-3 text-xs font-bold uppercase">Asset</th>
                                    <th className="px-6 py-3 text-xs font-bold uppercase text-right">Ann. Return</th>
                                    <th className="px-6 py-3 text-xs font-bold uppercase text-right">Ann. Volatility</th>
                                    <th className="px-6 py-3 text-xs font-bold uppercase text-center">Status vs {benchmark}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {results.map((res) => (
                                    <tr key={res.ticker} className="hover:bg-slate-50">
                                        <td className="px-6 py-3 font-bold text-slate-800">{res.ticker}</td>
                                        <td className="px-6 py-3 text-right font-mono text-sm">{(res.return * 100).toFixed(1)}%</td>
                                        <td className="px-6 py-3 text-right font-mono text-sm">{(res.volatility * 100).toFixed(1)}%</td>
                                        <td className="px-6 py-3 text-center">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold border ${getStatusColor(res.status)}`}>
                                                {res.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center text-slate-400 text-sm">
                        Select assets to analyze tail risk dominance.
                    </div>
                )}
            </div>
        </div>
    );
};

const KpiCard = ({ label, value, icon, color = 'text-slate-800' }: any) => (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-2 mb-2 text-slate-400 text-xs font-bold uppercase tracking-wider">
            {icon} {label}
        </div>
        <div className={`text-2xl font-mono font-bold ${color}`}>
            {value}
        </div>
    </div>
);

// ============================================================================
// MAIN COMPONENT: STRATEGY LAB WRAPPER
// ============================================================================
const StrategyLabView: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'OPTIMIZER' | 'ALPHA' | 'DOMINANCE'>('OPTIMIZER');

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
            {/* Header / Tab Switcher */}
            <div className="h-14 shrink-0 bg-white border-b border-slate-200 flex items-center px-6 gap-6">
                <div className="flex items-center gap-2 text-slate-800 font-bold">
                    <IconStrategy className="text-blue-600" />
                    Strategy Lab
                </div>
                <div className="h-6 w-px bg-slate-200"></div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => setActiveTab('OPTIMIZER')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                            activeTab === 'OPTIMIZER' 
                            ? 'bg-blue-600 text-white shadow-sm' 
                            : 'text-slate-500 hover:bg-slate-100'
                        }`}
                    >
                        Portfolio Optimizer
                    </button>
                    <button 
                        onClick={() => setActiveTab('ALPHA')}
                        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                            activeTab === 'ALPHA' 
                            ? 'bg-purple-600 text-white shadow-sm' 
                            : 'text-slate-500 hover:bg-slate-100'
                        }`}
                    >
                        Alpha Simulator <span className="ml-1 opacity-50 font-normal">NEW</span>
                    </button>
                    <button onClick={() => setActiveTab('DOMINANCE')} 
                        className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                            activeTab === 'DOMINANCE' 
                            ? 'bg-amber-600 text-white shadow-sm' 
                            : 'text-slate-500 hover:bg-slate-100'
                        }`}
                    >
                        Dominance Filter <span className="ml-1 opacity-50 font-normal">PRO</span>
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden relative">
                {activeTab === 'OPTIMIZER' ? <PortfolioOptimizer /> : 
                 activeTab === 'ALPHA' ? <AlphaSimulator /> :
                 <DominanceFilter />}
            </div>
        </div>
    );
};

export default StrategyLabView;